/**
 * App lock — local PIN / biometric storage and policy.
 *
 * ─── Where the secret lives ─────────────────────────────────────────────────
 *
 * The PIN never leaves the device and is never sent to the API. It is stored as
 * a salted, iterated SHA-256 digest in expo-secure-store (iOS Keychain /
 * Android Keystore-backed storage). The server only knows POLICY: whether the
 * nurse has enrolled, by when she must, and whether an admin has reset her.
 *
 * Hashing rather than storing the PIN verbatim is defence in depth. Anyone who
 * can read the secure store can also read the auth tokens sitting beside it, so
 * the hash is not what stops them reaching PHI. What it does stop is a short
 * numeric PIN — very often the nurse's bank card PIN — being recovered in plain
 * text from a device backup or a forensic dump.
 *
 * ─── Biometrics and old binaries ────────────────────────────────────────────
 *
 * `expo-local-authentication` IS a dependency, so autolinking puts the native
 * module into the next store build. But an OTA ships JS onto binaries that were
 * built before it existed, and on those the native side is simply absent.
 *
 * So the JS must ask the NATIVE registry whether the module is there, and must
 * do so without ever evaluating the package — see `getLocalAuth` below. That is
 * what makes one bundle safe for both populations: nurses on the store build
 * get biometrics, nurses on an older binary get the PIN and a "coming soon"
 * label, and everybody keeps receiving OTAs.
 */
import Constants from "expo-constants";
import { requireOptionalNativeModule } from "expo-modules-core";
import * as SecureStore from "expo-secure-store";
import { Platform } from "react-native";

import {
    NO_PIN_ATTEMPTS,
    parseAttempts,
    serializeAttempts,
    type PinAttemptState
} from "@/lib/app-lock-attempts";
import { pbkdf2ishHex } from "@/lib/crypto/sha256";
import { loadLocalAuthModule, type LocalAuthModule } from "@/lib/local-auth-loader";

// Cost factor for the stored digest. Measured at ~10ms per verification, which
// is unnoticeable once per unlock.
//
// Be honest about what that buys: ~100 guesses/sec against this same JS, so the
// 1,000,000-value six-digit space falls in a few hours, and far faster with a
// native or GPU SHA-256. This slows down a casual grind of a hash lifted from a
// device backup; it is NOT what makes the PIN safe. The real bounds are the OS
// secure store the digest sits in and the fact that anyone who can read it can
// also read the auth tokens next to it. Raise this, or move to a native KDF, if
// the threat model ever changes.
const PIN_HASH_ITERATIONS = 10_000;

export const PIN_LENGTH = 6;

const KEYS = {
    pinHash: "app-lock-pin-hash",
    pinSalt: "app-lock-pin-salt",
    method: "app-lock-method",
    /** The `resetAt` value last seen from the server, to detect admin resets. */
    seenResetAt: "app-lock-seen-reset-at",
    /** Wrong-PIN count and pause deadline, so a restart does not reset them. */
    pinAttempts: "app-lock-pin-attempts"
} as const;

// expo-secure-store is native-only. On web the app runs as an internal tool
// behind a desktop login, so localStorage matches how auth.tsx already degrades
// there rather than inventing a second story.
const storage = {
    get: (key: string) =>
        Platform.OS === "web"
            ? Promise.resolve(localStorage.getItem(key))
            : SecureStore.getItemAsync(key),
    set: (key: string, value: string) =>
        Platform.OS === "web"
            ? Promise.resolve(localStorage.setItem(key, value))
            : SecureStore.setItemAsync(key, value),
    del: (key: string) =>
        Platform.OS === "web"
            ? Promise.resolve(localStorage.removeItem(key))
            : SecureStore.deleteItemAsync(key)
};

export type AppLockMethod = "pin" | "biometric";

// ─── Biometric capability probe ─────────────────────────────────────────────

/**
 * First native build whose EAS binary carries the ExpoLocalAuthentication
 * module.
 *
 * Biometrics are refused below this build, INDEPENDENTLY of what the native
 * registry says, because on 2026-08-12 the registry lied. Eight iOS nurses were
 * offered Face ID and enrolled with it on a bundle whose native module had
 * never been in any shipped binary — `requireOptionalNativeModule` returned a
 * truthy stub for them (via its NativeModulesProxy / TurboModule fallbacks)
 * rather than null. Meanwhile other devices on the SAME bundle got null and
 * crashed. One bundle, two populations, split by a lookup we do not control.
 *
 * A build number is something we do control, so it is the outer gate: the
 * registry probe can only ever take biometrics away, never grant them to a
 * binary that provably cannot have the module. This is also literally the rule
 * we want — "only for people who installed from the store".
 *
 * 140 is the store build cut for the launch. The plugin has lived in app.config
 * since build 125, but the incident-era binaries around then did not reliably
 * carry the native side, so we gate on the fresh launch build rather than 125:
 * every binary numbered >= 140 is built from a config that includes the plugin,
 * so it provably has the module. One number gates BOTH platforms because the
 * deploy pipeline writes iOS `buildNumber` and Android `versionCode` from the
 * same value (see .github/workflows/deploy-mobile-native.yml). Below it the UI
 * shows "coming soon" and the PIN is used. Raise this only if a future build
 * ever ships without the module; the value is only ever a floor, so newer
 * builds (a bumped 141, 142, …) keep biometrics without further edits.
 */
const FIRST_BUILD_WITH_BIOMETRICS = 140;

/**
 * The running binary's native build number, or null when it cannot be read.
 *
 * Null is treated as "too old". Refusing on an unreadable value is the safe
 * direction: the cost is a nurse seeing "coming soon" when she could have had
 * Face ID, versus offering a prompt that cannot work.
 *
 * ─── Why not `Constants.nativeBuildVersion` ─────────────────────────────────
 *
 * That property was removed from expo-constants in 16.0.0 (SDK 51). On our SDK
 * it is always undefined, so until 2026-09-24 this returned null on EVERY
 * device and no nurse was ever offered biometrics: 301 enrolled, all on a PIN,
 * although the store has carried builds above the floor since early September.
 * The unit tests mocked the removed property, so they passed.
 *
 * Both sources below read the installed binary, never the OTA manifest
 * (`Constants.expoConfig.ios.buildNumber` would report the OTA's number):
 *   - iOS: expo-constants still exposes CFBundleVersion as
 *     `platform.ios.buildNumber`.
 *   - Android: expo-constants has no native equivalent, so ask ExpoApplication
 *     (linked through expo-notifications) for `nativeBuildVersion`, the
 *     installed versionCode. The registry probe returns null rather than
 *     throwing, and a stub without the constant lands on null as well.
 */
function nativeBuildNumber(): number | null {
    const raw =
        Platform.OS === "ios"
            ? Constants.platform?.ios?.buildNumber
            : requireOptionalNativeModule<{ nativeBuildVersion?: string | null }>("ExpoApplication")
                  ?.nativeBuildVersion;
    if (raw == null) {
        return null;
    }
    const n = typeof raw === "string" ? Number.parseInt(raw, 10) : Number(raw);
    return Number.isFinite(n) ? n : null;
}

// `undefined` = not probed yet, `null` = probed and unavailable.
let _localAuth: LocalAuthModule | null | undefined;

/**
 * Resolves expo-local-authentication only on binaries that actually carry the
 * native module.
 *
 * ─── INCIDENT 2026-08-12, and why this is written this way ──────────────────
 *
 * The previous version did `try { require("expo-local-authentication") }`,
 * assuming a missing native module surfaces as a catchable throw. The package's
 * entry point runs `requireNativeModule('ExpoLocalAuthentication')` AT MODULE
 * SCOPE, which throws on a binary without the native side — and importing the
 * package is what triggers it. 115 errors across 14 nurses.
 *
 * Note the OTA carrying that code shipped on 08-09 and was harmless for three
 * days: nothing imported the package until the app-lock feature switched on at
 * 08-12 04:00Z and the setup screen first rendered. A latent import is a live
 * hazard even when the feature is dark.
 *
 * `requireOptionalNativeModule` asks the native registry directly and returns
 * `null` instead of throwing. Critically it does NOT evaluate the package's
 * JavaScript, so on an old binary the dangerous module-scope call is never
 * reached. Only once the native side is confirmed present do we require the
 * package — at which point its top-level call cannot fail.
 *
 * This mirrors how the medication upload handles binaries whose
 * expo-file-system predates `createUploadTask`: check the capability, then
 * degrade. Never assume the installed binary matches the bundle.
 */
function getLocalAuth(): LocalAuthModule | null {
    if (_localAuth !== undefined) {
        return _localAuth;
    }
    try {
        // Outer gate: a binary older than the store build that carries the
        // module provably cannot have it, whatever the registry claims. This
        // runs FIRST so a lying registry can never open the door.
        const build = nativeBuildNumber();
        if (build === null || build < FIRST_BUILD_WITH_BIOMETRICS) {
            _localAuth = null;
            return _localAuth;
        }
        // Native-registry probe. Returns null on every binary built before
        // expo-local-authentication was added, and never throws.
        if (requireOptionalNativeModule("ExpoLocalAuthentication") === null) {
            _localAuth = null;
            return _localAuth;
        }
        // Safe now: the native module answered, so the package's module-scope
        // requireNativeModule() will resolve rather than throw.
        const mod = loadLocalAuthModule();
        _localAuth = typeof mod?.authenticateAsync === "function" ? mod : null;
    } catch {
        // Belt and braces. The probe above should make this unreachable, but a
        // biometric prompt is never worth a crash.
        _localAuth = null;
    }
    return _localAuth;
}

/**
 * True when this build can actually offer Face ID / fingerprint AND the nurse
 * has enrolled one with the OS. Any failure answers "no" so the UI falls back
 * to the PIN rather than presenting an option that cannot work.
 */
export async function isBiometricAvailable(): Promise<boolean> {
    const auth = getLocalAuth();
    if (!auth) {
        return false;
    }
    try {
        return (await auth.hasHardwareAsync()) && (await auth.isEnrolledAsync());
    } catch {
        return false;
    }
}

/** Prompts for biometric confirmation. Returns false on cancel or any error. */
export async function authenticateWithBiometrics(): Promise<boolean> {
    const auth = getLocalAuth();
    if (!auth) {
        return false;
    }
    try {
        const res = await auth.authenticateAsync({
            promptMessage: "Unlock Task Health",
            // Force our own PIN screen as the fallback rather than the device
            // passcode: the device passcode unlocks the phone, which is exactly
            // the thing we are adding a second factor on top of.
            disableDeviceFallback: true,
            cancelLabel: "Use PIN"
        });
        return res?.success === true;
    } catch {
        return false;
    }
}

// ─── PIN storage ────────────────────────────────────────────────────────────

/**
 * Salt for the stored digest. Uniqueness is what matters here (it stops one
 * precomputed table covering every nurse), not unpredictability, so falling
 * back to Math.random on a runtime without WebCrypto is acceptable.
 */
function generateSalt(): string {
    const g = globalThis as { crypto?: { getRandomValues?: (a: Uint8Array) => Uint8Array } };
    if (g.crypto?.getRandomValues) {
        const bytes = new Uint8Array(16);
        g.crypto.getRandomValues(bytes);
        return Array.from(bytes)
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("");
    }
    return `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`;
}

export async function isPinSet(): Promise<boolean> {
    const [hash, salt] = await Promise.all([storage.get(KEYS.pinHash), storage.get(KEYS.pinSalt)]);
    return Boolean(hash && salt);
}

export async function getStoredMethod(): Promise<AppLockMethod | null> {
    const m = await storage.get(KEYS.method);
    return m === "pin" || m === "biometric" ? m : null;
}

/** Persists a new PIN. `method` records whether biometrics are preferred. */
export async function setPin(pin: string, method: AppLockMethod = "pin"): Promise<void> {
    const salt = generateSalt();
    const hash = pbkdf2ishHex(pin, salt, PIN_HASH_ITERATIONS);
    await storage.set(KEYS.pinSalt, salt);
    await storage.set(KEYS.pinHash, hash);
    await storage.set(KEYS.method, method);
    // A new PIN starts with a clean count, as a fresh lock screen always did.
    await clearPinAttempts();
}

/** Constant-ish time comparison. Both operands are fixed-length hex digests. */
function digestsEqual(a: string, b: string): boolean {
    if (a.length !== b.length) {
        return false;
    }
    let diff = 0;
    for (let i = 0; i < a.length; i++) {
        diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
    }
    return diff === 0;
}

export async function verifyPin(pin: string): Promise<boolean> {
    const [hash, salt] = await Promise.all([storage.get(KEYS.pinHash), storage.get(KEYS.pinSalt)]);
    if (!hash || !salt) {
        return false;
    }
    return digestsEqual(pbkdf2ishHex(pin, salt, PIN_HASH_ITERATIONS), hash);
}

/** Wipes the local lock. Used on logout and on an admin-initiated reset. */
export async function clearAppLock(): Promise<void> {
    await Promise.all([
        storage.del(KEYS.pinHash).catch(() => {}),
        storage.del(KEYS.pinSalt).catch(() => {}),
        storage.del(KEYS.method).catch(() => {}),
        clearPinAttempts()
    ]);
}

// ─── Wrong-PIN count ────────────────────────────────────────────────────────
//
// The rules live in lib/app-lock-attempts.ts. These three only move the state
// in and out of the secure store, and never throw: a store that cannot be read
// counts as "no wrong PINs yet", which is exactly the behaviour before the
// count was saved, so a storage problem can never lock anyone out.

/** The saved wrong-PIN state, or "none" when nothing (readable) is saved. */
export async function loadPinAttempts(now: number = Date.now()): Promise<PinAttemptState> {
    try {
        return parseAttempts(await storage.get(KEYS.pinAttempts), now);
    } catch {
        return { ...NO_PIN_ATTEMPTS };
    }
}

export async function savePinAttempts(state: PinAttemptState): Promise<void> {
    try {
        if (state.failures === 0 && state.blockedUntil === 0) {
            await storage.del(KEYS.pinAttempts);
        } else {
            await storage.set(KEYS.pinAttempts, serializeAttempts(state));
        }
    } catch {
        // Best effort: the in-memory count still applies to this session.
    }
}

export async function clearPinAttempts(): Promise<void> {
    try {
        await storage.del(KEYS.pinAttempts);
    } catch {
        // Best effort, as above.
    }
}

// ─── Admin reset tracking ───────────────────────────────────────────────────

/**
 * Applies an admin reset, if the server's `resetAt` is newer than the one this
 * device has already acted on.
 *
 * Comparing timestamps rather than consuming a boolean flag keeps this correct
 * for a nurse with two devices: each one applies the same reset independently,
 * and neither can clear it out from under the other.
 *
 * Returns true when a reset was applied (the caller should route into setup).
 */
export async function applyServerResetIfNewer(serverResetAt: string | null): Promise<boolean> {
    if (!serverResetAt) {
        return false;
    }
    const seen = await storage.get(KEYS.seenResetAt);
    if (seen === serverResetAt) {
        return false;
    }
    // First run after a fresh install also lands here; clearing an empty lock
    // is harmless and it records the marker so it does not repeat.
    await clearAppLock();
    await storage.set(KEYS.seenResetAt, serverResetAt);
    return true;
}

/** Forgets reset bookkeeping. Logout only — never on a lock reset. */
export async function clearResetMarker(): Promise<void> {
    await storage.del(KEYS.seenResetAt).catch(() => {});
}
